
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import RichTextEditor from "../editor/RichTextEditor";
import ArticleEditorTitle from "./ArticleEditorTitle";
import ArticleEditorSlug from "./ArticleEditorSlug";
import ArticleEditorCategory from "./ArticleEditorCategory";
import ArticleEditorTopic from "./ArticleEditorTopic";
import ArticleEditorPublicationState from "./ArticleEditorPublicationState";
import ArticleEditorImage from "./ArticleEditorImage";
import { useArticleCategories } from "./hooks/useArticleCategories";
import { uploadArticleImage } from "./hooks/useImageUploader";
import { Form } from "@/components/ui/form"; // IMPORT Form
import { Button } from "@/components/ui/button"; // IMPORT Button

// Export type
export interface ArticleFormValues {
  title: string;
  content: string;
  category_id: string;
  topic: string;
  image_file?: FileList;
  publication_state: string;
  slug?: string;
}

interface Category {
  id: string;
  name: string;
}

interface ArticleEditorProps {
  article?: any;
  onSaveComplete: () => void;
}

export default function ArticleEditor({ article, onSaveComplete }: ArticleEditorProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [publicationState, setPublicationState] = useState<string>(article?.publication_state || "draft");
  const [isLoading, setIsLoading] = useState(true);
  const [currentSlug, setCurrentSlug] = useState<string>(article?.slug || "");
  const [editorContent, setEditorContent] = useState<string>(article?.content || "");

  // Form initialization
  const form = useForm<ArticleFormValues>({
    defaultValues: {
      title: article?.title || "",
      content: article?.content || "",
      category_id: article?.category_id || "",
      topic: article?.topic || "",
      publication_state: article?.publication_state || "draft",
      slug: article?.slug || "",
    }
  });

  // Categories
  const { categories, loading: categoriesLoading } = useArticleCategories();

  // Fetch article data if article ID is provided (editing mode)
  useEffect(() => {
    if (article?.id) {
      fetchArticleData(article.id);
    } else {
      setIsLoading(false);
    }
  }, [article]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "title" && !article?.id) {
        const { generateSlug } = require("@/lib/utils"); // dynamic import; avoids import cycle
        const newSlug = generateSlug(value.title || "");
        setCurrentSlug(newSlug);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, article]);

  const fetchArticleData = async (articleId: string) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("id", articleId)
        .single();

      if (error) {
        console.error("Error fetching complete article data:", error);
        toast.error("Failed to load article data");
        throw error;
      }

      if (data) {
        form.reset({
          title: data.title || "",
          content: data.content || "",
          category_id: data.category_id || "",
          topic: data.topic || "",
          publication_state: data.publication_state || "draft",
          slug: data.slug || "",
        });

        setPublicationState(data.publication_state || "draft");
        setCurrentSlug(data.slug || "");
        setEditorContent(data.content || "");

        if (data.image_url) {
          setImagePreview(data.image_url);
          const urlParts = data.image_url.split('/');
          const filename = urlParts[urlParts.length - 1];
          setSelectedFileName(filename);
        }
      }
    } catch (error) {
      console.error("Error in fetchArticleData:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (formData: ArticleFormValues) => {
    setIsSubmitting(true);

    try {
      formData.publication_state = publicationState;
      const slug = currentSlug || (await import("@/lib/utils")).generateSlug(formData.title);
      formData.content = editorContent;

      let imageUrl = article?.image_url || null;

      if (formData.image_file && formData.image_file.length > 0) {
        try {
          imageUrl = await uploadArticleImage(formData.image_file[0]);
        } catch (error: any) {
          toast.error(`Image upload failed: ${error.message}`);
        }
      } else if (imagePreview && !article?.image_url) {
        imageUrl = imagePreview;
      }

      const articleData = {
        title: formData.title,
        content: formData.content,
        category_id: formData.category_id,
        topic: formData.topic,
        publication_state: formData.publication_state,
        slug,
        image_url: imageUrl,
        date: article?.date || new Date().toISOString(),
      };

      if (article) {
        const { error } = await supabase
          .from("articles")
          .update(articleData)
          .eq("id", article.id);
        if (error) throw error;
        toast.success("Article updated successfully");
      } else {
        const { error } = await supabase
          .from("articles")
          .insert([articleData]);
        if (error) throw error;
        toast.success("Article created successfully");
      }

      onSaveComplete();
    } catch (error: any) {
      toast.error(`Failed to save article: ${error.message || "Unknown error"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const removeSelectedImage = () => {
    setImagePreview(null);
    setSelectedFileName(null);
    const fileInput = document.getElementById('image-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
    if (article?.image_url) {
      form.setValue('image_file', undefined);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-black/20 border border-white/10 rounded-lg p-6">
        <p className="text-white">Loading article data...</p>
      </div>
    );
  }

  return (
    <div className="bg-black/20 border border-white/10 rounded-lg p-6 shadow-lg">
      <h2 className="text-xl font-bold mb-6 glow-text">
        {article ? "Edit Article" : "Create New Article"}
      </h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <ArticleEditorTitle control={form.control} />
          <ArticleEditorSlug article={article} currentSlug={currentSlug} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ArticleEditorCategory control={form.control} categories={categories} />
            <ArticleEditorTopic control={form.control} />
          </div>
          <ArticleEditorPublicationState
            control={form.control}
            publicationState={publicationState}
            setPublicationState={setPublicationState}
          />
          <ArticleEditorImage
            control={form.control}
            imagePreview={imagePreview}
            selectedFileName={selectedFileName}
            handleImageChange={handleImageChange}
            removeSelectedImage={removeSelectedImage}
          />
          <div>
            <RichTextEditor
              value={editorContent}
              onChange={setEditorContent}
              placeholder="Start writing your article..."
            />
            {/* Form message for content validation */}
            <div className="mt-1 text-destructive text-sm">
              {form.formState.errors.content?.message}
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={onSaveComplete}
              className="border-white/20 hover:bg-white/5 text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-black/50 hover:bg-black/70 text-white"
            >
              {isSubmitting ? "Saving..." : (article ? "Update Article" : "Create Article")}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
